import React, { useEffect, useState, useRef } from "react";
import { Users, ShoppingBag, Package, Star } from "lucide-react";
import { motion, useInView } from "framer-motion";

// --- Smart Counter Component ---
const Counter = ({ target, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const nodeRef = useRef(null);
  const isInView = useInView(nodeRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;

    let startTime;
    let animationFrame;

    const updateCounter = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;

      // Calculate percentage complete (0 to 1)
      const percentage = Math.min(progress / duration, 1);
      
      // Easing function (easeOutExpo) for smooth landing
      const easeOut = 1 - Math.pow(2, -10 * percentage);

      setCount(Math.floor(easeOut * target));

      if (progress < duration) {
        animationFrame = requestAnimationFrame(updateCounter);
      } else {
        setCount(target); // Ensure it lands exactly on target
      }
    };

    animationFrame = requestAnimationFrame(updateCounter);

    return () => cancelAnimationFrame(animationFrame);
  }, [target, duration, isInView]);

  return (
    <span ref={nodeRef} className="tabular-nums">
      {count}
    </span>
  );
};

// --- Main Component ---
export default function Detailbar() {
  const stats = [
    { 
      id: 1, 
      label: "Retailers Onboarded", 
      value: 4500, 
      icon: <ShoppingBag size={24} />,
      suffix: "+"
    },
    { 
      id: 2, 
      label: "Happy Customers", 
      value: 4000, 
      icon: <Users size={24} />,
      suffix: "+"
    },
    { 
      id: 3, 
      label: "Products Supplied", 
      value: 5000, 
      icon: <Package size={24} />,
      suffix: "+"
    },
    { 
      id: 4, 
      label: "Premium Brands", 
      value: 5, 
      icon: <Star size={24} />,
      suffix: "+"
    },
  ];

  return (
    <section className="relative bg-yellow-500 py-10 overflow-hidden">
      
      {/* Background Texture/Pattern */}
      <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      
      {/* Glossy Overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 divide-y md:divide-y-0 md:divide-x divide-black/10">
          
          {stats.map((stat, index) => (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className={`flex flex-col items-center justify-center text-center p-4 ${index > 1 ? "pt-8 md:pt-4" : ""}`}
            >
              {/* Icon Bubble */}
              <div className="mb-3 p-3 bg-black text-yellow-500 rounded-full shadow-lg transform hover:scale-110 transition-transform duration-300">
                {stat.icon}
              </div>

              {/* Counter Number */}
              <h3 className="text-3xl md:text-4xl font-black text-black flex items-center gap-0.5">
                <Counter target={stat.value} />
                <span className="text-2xl text-black/70">{stat.suffix}</span>
              </h3>

              {/* Label */}
              <p className="text-sm md:text-base font-bold text-neutral-800 uppercase tracking-wide mt-1">
                {stat.label}
              </p>
            </motion.div>
          ))}
          
        </div>
      </div>
    </section>
  );
}